<template>
  <div class="reporting-section" v-if="hasAnyData">
    <div class="section-header">
      <i class="material-icons section-icon">people</i>
      <h2>{{ sectionTitle }}</h2>
    </div>

    <!-- Persona summary -->
    <div class="persona-block" v-if="personaSummary">
      <div class="block-title">{{ $t('dash.reporting.section.demographie.subtitle.profil_type') }}</div>
      <div class="persona-chips">
        <div class="persona-chip" v-if="personaSummary.gender">
          <div class="chip-icon gender-icon" :style="{ background: personaSummary.gender.bg }">
            <span class="gender-symbol" :style="{ color: personaSummary.gender.iconColor }">{{ personaSummary.gender.icon }}</span>
          </div>
          <div class="chip-body">
            <span class="chip-label">{{ $t('dash.reporting.section.demographie.genre') }}</span>
            <span class="chip-value">{{ personaSummary.gender.name }}</span>
            <span class="chip-pct">{{ personaSummary.gender.pct }}%</span>
          </div>
        </div>
        <div class="persona-chip" v-if="personaSummary.age">
          <div class="chip-icon age-icon">
            <i class="material-icons" style="color:#6B1F4D;font-size:20px">cake</i>
          </div>
          <div class="chip-body">
            <span class="chip-label">Âge</span>
            <span class="chip-value">{{ personaSummary.age.name }}</span>
            <span class="chip-pct">{{ personaSummary.age.pct }}%</span>
          </div>
        </div>
        <div class="persona-chip" v-if="personaSummary.country">
          <div class="chip-icon country-icon">
            <img
              v-if="personaSummary.country.code"
              :src="`https://flagcdn.com/h40/${personaSummary.country.code.toLowerCase()}.png`"
              class="country-flag-img"
              alt=""
            >
            <i v-else class="material-icons" style="color:#10B981;font-size:20px">place</i>
          </div>
          <div class="chip-body">
            <span class="chip-label">{{ $t('dash.reporting.top_countries') }}</span>
            <span class="chip-value">{{ personaSummary.country.name }}</span>
            <span class="chip-pct">{{ personaSummary.country.pct }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Genre distribution -->
    <div class="demo-block" v-if="personaGenderData.length">
      <div class="block-title">{{ $t('dash.reporting.section.demographie.genre') }}</div>
      <div class="gender-list">
        <div class="gender-item" v-for="g in personaGenderData" :key="g.key">
          <span class="gender-label">{{ g.name }}</span>
          <div class="gender-bar-wrap">
            <div class="gender-bar" :style="{ width: g.pct + '%', background: g.color }"></div>
          </div>
          <span class="gender-pct">{{ g.pct }}%</span>
        </div>
      </div>
    </div>

    <!-- Âge distribution -->
    <div class="demo-block" v-if="personaAgeData.length">
      <div class="block-title">Âge</div>
      <div class="country-list">
        <template v-for="(item, idx) in personaAgeData">
          <span class="country-rank" :key="'ar-'+idx">{{ idx + 1 }}</span>
          <span class="country-name" :key="'an-'+idx">{{ item.name }}</span>
          <div class="country-bar-wrap" :key="'ab-'+idx">
            <div class="country-bar age-bar" :style="{ width: item.barWidth + '%' }"></div>
          </div>
          <span class="country-pct" :key="'ap-'+idx">{{ item.pct }}%</span>
        </template>
      </div>
    </div>

    <!-- Carte monde + Top 10 pays -->
    <div class="demo-block" v-if="topCountries.length">
      <div class="block-title">{{ $t('dash.reporting.top_countries') }}</div>

      <div class="map-wrap" v-if="countryMapData">
        <MapChart
          :countryData="countryMapData"
          :LangUser="mapLang"
          :currency-add="false"
          highColor="rgba(107, 31, 77, 1)"
          lowColor="rgba(107, 31, 77, 0.08)"
          countryStrokeColor="#ffffff"
          defaultCountryFillColor="#e8e4ec"
        />
      </div>

      <div class="country-list">
        <template v-for="(item, idx) in topCountries">
          <span class="country-rank" :key="'rank-'+idx">{{ idx + 1 }}</span>
          <span class="country-name" :key="'name-'+idx">{{ item.name }}</span>
          <div class="country-bar-wrap" :key="'bar-'+idx">
            <div class="country-bar" :style="{ width: item.barWidth + '%' }"></div>
          </div>
          <span class="country-pct" :key="'pct-'+idx">{{ item.pct }}%</span>
        </template>
      </div>
    </div>

    <!-- Villes top -->
    <div class="demo-block" v-if="topCities.length">
      <div class="block-title">Top villes</div>
      <div class="country-list">
        <template v-for="(item, idx) in topCities">
          <span class="country-rank" :key="'rank-'+idx">{{ idx + 1 }}</span>
          <span class="country-name" :key="'name-'+idx">{{ item.name }}</span>
          <div class="country-bar-wrap" :key="'bar-'+idx">
            <div class="country-bar" :style="{ width: item.barWidth + '%' }"></div>
          </div>
          <span class="country-pct" :key="'pct-'+idx">{{ item.pct }}%</span>
        </template>
      </div>
    </div>

    <!-- LinkedIn : industries -->
    <div class="demo-block" v-if="topIndustries.length">
      <div class="block-title">{{ $t('dash.reporting.section.demographie.industries') }}</div>
      <div class="country-list">
        <template v-for="(item, idx) in topIndustries">
          <span class="country-rank" :key="'rank-'+idx">{{ idx + 1 }}</span>
          <span class="country-name" :key="'name-'+idx">{{ item.name }}</span>
          <div class="country-bar-wrap" :key="'bar-'+idx">
            <div class="country-bar li-bar" :style="{ width: item.barWidth + '%' }"></div>
          </div>
          <span class="country-pct" :key="'pct-'+idx">{{ item.pct }}%</span>
        </template>
      </div>
    </div>

    <!-- LinkedIn : seniority -->
    <div class="demo-block" v-if="topSeniority.length">
      <div class="block-title">{{ $t('dash.reporting.section.demographie.seniority') }}</div>
      <div class="country-list">
        <template v-for="(item, idx) in topSeniority">
          <span class="country-rank" :key="'rank-'+idx">{{ idx + 1 }}</span>
          <span class="country-name" :key="'name-'+idx">{{ item.name }}</span>
          <div class="country-bar-wrap" :key="'bar-'+idx">
            <div class="country-bar li-seniority-bar" :style="{ width: item.barWidth + '%' }"></div>
          </div>
          <span class="country-pct" :key="'pct-'+idx">{{ item.pct }}%</span>
        </template>
      </div>
    </div>

    <!-- LinkedIn : fonctions -->
    <div class="demo-block" v-if="topFunctions.length">
      <div class="block-title">{{ $t('dash.reporting.section.demographie.function') }}</div>
      <div class="country-list">
        <template v-for="(item, idx) in topFunctions">
          <span class="country-rank" :key="'rank-'+idx">{{ idx + 1 }}</span>
          <span class="country-name" :key="'name-'+idx">{{ item.name }}</span>
          <div class="country-bar-wrap" :key="'bar-'+idx">
            <div class="country-bar li-function-bar" :style="{ width: item.barWidth + '%' }"></div>
          </div>
          <span class="country-pct" :key="'pct-'+idx">{{ item.pct }}%</span>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import MapChart from 'vue-chart-map'
import isoCountries from 'i18n-iso-countries'
isoCountries.registerLocale(require('i18n-iso-countries/langs/fr.json'))
isoCountries.registerLocale(require('i18n-iso-countries/langs/en.json'))
isoCountries.registerLocale(require('i18n-iso-countries/langs/de.json'))
isoCountries.registerLocale(require('i18n-iso-countries/langs/es.json'))
isoCountries.registerLocale(require('i18n-iso-countries/langs/it.json'))
isoCountries.registerLocale(require('i18n-iso-countries/langs/pt.json'))

export default {
  name: 'ReportingDemographics',
  components: { MapChart },
  props: {
    stats: { type: Object, required: true },
    page: { type: Object, default: null },
  },
  computed: {
    pageId() {
      return this.page ? this.page.id : 'demo'
    },
    network() {
      return this.page ? (this.page.network || '') : ''
    },
    mapLang() {
      var locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale.substring(0, 2) : 'en'
      return isoCountries.isValid ? locale : 'en'
    },
    sectionTitle() {
      var key = 'dash.reporting.section.demographie.title.' + this.network
      var t = this.$t(key)
      return t !== key ? t : this.$t('dash.reporting.section.demographie.title')
    },
    // Gender breakdown — tries audience_gender (new IG API), then audience_by_gender (old IG),
    // then computes from page_fans_gender_age (Facebook flat object)
    personaGenderData() {
      var p = this.stats.page
      var self = this
      var LABELS = {
        M: self.$t('dash.reporting.section.demographie.type_man'),
        male: self.$t('dash.reporting.section.demographie.type_man'),
        F: self.$t('dash.reporting.section.demographie.type_female'),
        female: self.$t('dash.reporting.section.demographie.type_female'),
        U: self.$t('dash.reporting.section.demographie.type_undefined'),
        undifined: self.$t('dash.reporting.section.demographie.type_undefined'),
        UNKNOWN: self.$t('dash.reporting.section.demographie.type_undefined'),
      }
      var COLORS = { M: '#4299E1', male: '#4299E1', F: '#F687B3', female: '#F687B3', U: '#9ba3b7', undifined: '#9ba3b7', UNKNOWN: '#9ba3b7' }

      var raw = null

      // 1. audience_gender [{name:'M'|'F'|'U', value:N}] — new IG API breakdown(gender)
      if (Array.isArray(p.audience_gender) && p.audience_gender.length) {
        raw = p.audience_gender
      }
      // 2. audience_by_gender — derived from old IG format
      else if (Array.isArray(p.audience_by_gender) && p.audience_by_gender.length) {
        raw = p.audience_by_gender
      }
      // 3. page_fans_gender_age flat object {'M.18-24': N, 'F.18-24': N} — Facebook
      else if (p.page_fans_gender_age && typeof p.page_fans_gender_age === 'object') {
        var ga = p.page_fans_gender_age
        var mTotal = 0, fTotal = 0
        Object.keys(ga).forEach(function(k) {
          if (k.startsWith('M.')) mTotal += (ga[k] || 0)
          else if (k.startsWith('F.')) fTotal += (ga[k] || 0)
        })
        if (mTotal + fTotal > 0) raw = [{ name: 'M', value: mTotal }, { name: 'F', value: fTotal }]
      }
      // 4. page_follows_by_gender [{name:'male'|'female'|'undifined', value:N}] — TikTok
      else if (Array.isArray(p.page_follows_by_gender) && p.page_follows_by_gender.length) {
        raw = p.page_follows_by_gender
      }

      if (!raw || !raw.length) return []
      var total = raw.reduce(function(s, e) { return s + (e.value || 0) }, 0)
      if (!total) return []

      return raw
        .filter(function(e) { return e.value > 0 })
        .sort(function(a, b) { return b.value - a.value })
        .map(function(e) {
          return {
            key: e.name,
            name: LABELS[e.name] || e.name,
            pct: Math.round(e.value / total * 100),
            color: COLORS[e.name] || '#9ba3b7',
          }
        })
    },
    // Age distribution — aggregates audience_gender_age regardless of format
    // New IG: [{name:'18-24', value:N}], Old IG: [{name:'M.18-24', value:N}]
    personaAgeData() {
      var p = this.stats.page
      var raw = p.audience_gender_age
        || p.page_follows_gender_age
        || p.fans_gender_age
        || null
      if (!Array.isArray(raw) || !raw.length) return []

      // Aggregate by age bucket (strip gender prefix if present)
      var ageMap = {}
      raw.forEach(function(e) {
        if (!e.name) return
        var ageName = e.name.includes('.') ? e.name.split('.')[1] : e.name
        if (!ageName) return
        ageMap[ageName] = (ageMap[ageName] || 0) + (e.value || 0)
      })

      var total = Object.values(ageMap).reduce(function(s, v) { return s + v }, 0)
      if (!total) return []

      var entries = Object.keys(ageMap)
        .map(function(k) { return { name: k, value: ageMap[k] } })
        .filter(function(e) { return e.value > 0 })
        .sort(function(a, b) { return b.value - a.value })
        .slice(0, 6)
      var maxVal = entries.length ? entries[0].value : 1

      return entries.map(function(e) {
        return {
          name: e.name,
          pct: Math.round(e.value / total * 100),
          barWidth: Math.round(e.value / maxVal * 100),
        }
      })
    },
    personaSummary() {
      var gender = this.personaGenderData.length ? this.personaGenderData[0] : null
      var age = this.personaAgeData.length ? this.personaAgeData[0] : null
      var country = this.topCountries.length ? this.topCountries[0] : null
      if (!gender && !age && !country) return null
      if (gender) {
        var isM = gender.key === 'M' || gender.key === 'male'
        var isF = gender.key === 'F' || gender.key === 'female'
        gender = Object.assign({}, gender, {
          icon: isM ? '♂' : (isF ? '♀' : '⚧'),
          bg: isM ? 'rgba(66,153,225,0.12)' : (isF ? 'rgba(246,135,179,0.12)' : 'rgba(155,163,183,0.12)'),
          iconColor: isM ? '#4299E1' : (isF ? '#F687B3' : '#9ba3b7'),
        })
      }
      return { gender: gender, age: age, country: country }
    },
    rawCountryData() {
      return this.stats.page.page_follows_country_percent
        || this.stats.page.page_fans_country
        || this.stats.page.audience_country_percent
        || this.stats.page.audience_country
        || null
    },
    countryMapData() {
      if (!this.rawCountryData) return null
      return this.rawCountryData
    },
    topCountries() {
      if (!this.rawCountryData) return []
      var lang = this.mapLang
      var entries = Object.entries(this.rawCountryData)
        .map(function(e) {
          var val = parseFloat(e[1])
          return { code: e[0], name: isoCountries.getName(e[0], lang) || e[0], value: isNaN(val) ? 0 : val }
        })
        .filter(function(e) { return e.value > 0 })
        .sort(function(a, b) { return b.value - a.value })
        .slice(0, 10)
      var maxVal = entries.length ? entries[0].value : 1
      return entries.map(function(e) {
        return {
          code: e.code,
          name: e.name,
          barWidth: Math.round(e.value / maxVal * 100),
          pct: e.value < 1 ? e.value.toFixed(1) : Math.round(e.value),
        }
      })
    },
    topCities() {
      var raw = this.stats.page.page_fans_city || this.stats.page.audience_city
      if (!Array.isArray(raw) || !raw.length) return []
      var entries = raw
        .filter(function(e) { return e.value > 0 })
        .sort(function(a, b) { return b.value - a.value })
        .slice(0, 10)
      var maxVal = entries.length ? entries[0].value : 1
      var total = raw.reduce(function(s, e) { return s + (e.value || 0) }, 0)
      return entries.map(function(e) {
        var pct = total > 0 ? e.value / total * 100 : 0
        return {
          name: e.name,
          barWidth: Math.round(e.value / maxVal * 100),
          pct: pct < 1 ? pct.toFixed(1) : Math.round(pct),
        }
      })
    },
    topIndustries() {
      var raw = this.stats.page.page_fans_industry || null
      if (!raw || !raw.length) return []
      var total = raw.reduce(function(s, e) { return s + (e.value || 0) }, 0)
      if (!total) return []
      var entries = raw
        .map(function(e) { return { name: e.name, value: e.value || 0, pct: Math.round((e.value / total) * 100) } })
        .filter(function(e) { return e.pct > 0 })
        .slice(0, 8)
      var maxPct = entries.length ? entries[0].pct : 1
      return entries.map(function(e) { return { name: e.name, pct: e.pct, barWidth: Math.round(e.pct / maxPct * 100) } })
    },
    topSeniority() {
      var raw = this.stats.page.page_fans_seniority || null
      if (!raw || !raw.length) return []
      var total = raw.reduce(function(s, e) { return s + (e.value || 0) }, 0)
      if (!total) return []
      var entries = raw
        .map(function(e) { return { name: e.name, value: e.value || 0, pct: Math.round((e.value / total) * 100) } })
        .filter(function(e) { return e.pct > 0 })
        .slice(0, 8)
      var maxPct = entries.length ? entries[0].pct : 1
      return entries.map(function(e) { return { name: e.name, pct: e.pct, barWidth: Math.round(e.pct / maxPct * 100) } })
    },
    topFunctions() {
      var raw = this.stats.page.page_fans_function || null
      if (!raw || !raw.length) return []
      var total = raw.reduce(function(s, e) { return s + (e.value || 0) }, 0)
      if (!total) return []
      var entries = raw
        .map(function(e) { return { name: e.name, value: e.value || 0, pct: Math.round((e.value / total) * 100) } })
        .filter(function(e) { return e.pct > 0 })
        .slice(0, 8)
      var maxPct = entries.length ? entries[0].pct : 1
      return entries.map(function(e) { return { name: e.name, pct: e.pct, barWidth: Math.round(e.pct / maxPct * 100) } })
    },
    hasAnyData() {
      return this.personaGenderData.length > 0 || this.personaAgeData.length > 0
        || this.topCountries.length > 0 || this.topCities.length > 0
        || this.topIndustries.length > 0 || this.topSeniority.length > 0
        || this.topFunctions.length > 0
    },
  },
  methods: {},
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}
.section-icon { color: #6B1F4D; font-size: 22px; }
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; }

.demo-block {
  margin-bottom: 28px;
}
.demo-block:last-child { margin-bottom: 0; }
.block-title {
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 12px;
}

/* Persona summary */
.persona-block {
  margin-bottom: 28px;
}
.persona-chips {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.persona-chip {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #faf8fc;
  border: 1px solid #f1edf5;
  border-radius: 5.6px;
  padding: 12px 16px;
  flex: 1;
  min-width: 140px;
}
.chip-icon {
  width: 38px;
  height: 38px;
  border-radius: 5.6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.gender-symbol { font-size: 20px; font-weight: 700; line-height: 1; }
.age-icon { background: rgba(107,31,77,0.08); }
.country-icon { background: transparent; overflow: visible; display: flex; align-items: center; justify-content: center; }
.country-flag-img { height: 28px; width: auto; border-radius: 3px; display: block; box-shadow: 0 1px 4px rgba(0,0,0,0.15); }
.chip-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.chip-label {
  font-size: 10px;
  font-weight: 600;
  color: #9ba3b7;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1;
  margin-bottom: 3px;
}
.chip-value {
  font-size: 13px;
  font-weight: 700;
  color: #121826;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.chip-pct {
  font-size: 12px;
  color: #9ba3b7;
  font-weight: 500;
  margin-top: 1px;
}

/* Gender bars */
.gender-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.gender-item {
  display: grid;
  grid-template-columns: 80px 1fr 44px;
  align-items: center;
  gap: 10px;
}
.gender-label {
  font-size: 13px;
  font-weight: 600;
  color: #555E75;
}
.gender-bar-wrap {
  height: 8px;
  background: #f1edf5;
  border-radius: 4px;
  overflow: hidden;
}
.gender-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.4s ease;
}
.gender-pct {
  font-size: 12px;
  color: #9ba3b7;
  text-align: right;
}

.map-wrap {
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  background: #faf8fc;
  padding: 8px;
}

.country-list {
  display: grid;
  grid-template-columns: 20px max-content 1fr 44px;
  gap: 7px 12px;
  align-items: center;
}
.country-rank {
  font-size: 11px;
  font-weight: 700;
  color: #c0a8d0;
  text-align: right;
}
.country-name {
  font-size: 13px;
  font-weight: 600;
  color: #555E75;
  white-space: nowrap;
}
.country-bar-wrap {
  flex: 1;
  height: 6px;
  background: #f1edf5;
  border-radius: 3px;
  overflow: hidden;
}
.country-bar {
  height: 100%;
  background: #6B1F4D;
  border-radius: 3px;
  transition: width 0.4s ease;
}
.age-bar { background: rgba(107,31,77,0.5); }
.li-bar { background: #0077b5; }
.li-seniority-bar { background: #00a0dc; }
.li-function-bar { background: #057642; }
.country-pct { font-size: 12px; color: #9ba3b7; width: 36px; text-align: right; flex-shrink: 0; }
</style>
